import React from "react";
import classNames from "classnames/bind";
import styles from "./Button.module.scss";
import { detectEnv } from "../../utils/platform"; // ⬅️ OS/브라우저 감지
import { openAndroidAppOrStore, openIOSAppOrStore } from "../../utils/deeplink"; // ⬅️ 딥링크 유틸

const cx = classNames.bind(styles);

export default function Button({ data }) {
  // 서버에서 내려오는 스토어 URL(있으면 우선)
  const ANDROID_URL = data?.common?.playstoreUrl; // 내부 테스트 URL https://play.google.com/store/apps/details?id=com.prinmann.xinvana
  const IOS_URL = data?.common?.iosStoreUrl; // iOS 배포 후 채워질 예정
  const IOS_DEPLOYED = Boolean(IOS_URL); // 배포 여부 판단(임시)

  // 새 탭 의도(⌘/Ctrl 클릭 or 마우스 중클릭) 헬퍼
  const isNewTabIntent = (e) => e?.metaKey || e?.ctrlKey || e?.button === 1;

  // --- Android 버튼 ---
  const handleAndroidClick = (e) => {
    const env = detectEnv();

    // macOS 또는 iOS에서 "Android 다운로드" 클릭 → 예외
    if (env.isIOS || env.isMac) {
      alert(
        "Android 전용 링크입니다. iOS/macOS에서는 사용할 수 없습니다.\n'iOS 다운로드' 버튼을 이용해주세요.",
      );
      return;
    }

    // 데스크톱에서 새 탭 의도
    if (env.isDesktop && isNewTabIntent(e)) {
      if (ANDROID_URL)
        window.open(ANDROID_URL, "_blank", "noopener,noreferrer");
      return;
    }

    if (env.isAndroid) {
      // Android 단말: 앱 실행 시도 → 실패 시 내부 테스트 URL로 자동 폴백
      openAndroidAppOrStore({ playUrlOverride: ANDROID_URL });
    } else if (env.isWindows) {
      // Windows 데스크톱: 내부 테스트 페이지를 새 탭으로
      if (ANDROID_URL)
        window.open(ANDROID_URL, "_blank", "noopener,noreferrer");
      else alert("Android URL이 없습니다.");
    } else {
      // 그 외(리눅스 등) 데스크톱: 안내
      if (ANDROID_URL) {
        const ok = window.confirm(
          "이 링크는 Android 전용입니다.\n새 탭으로 테스트 페이지를 여시겠습니까?",
        );
        if (ok) window.open(ANDROID_URL, "_blank", "noopener,noreferrer");
      } else {
        alert("Android URL이 없습니다.");
      }
    }
  };

  const handleAndroidAuxClick = (e) => {
    // 마우스 중클릭 시 Windows 등 데스크톱에서 새 탭으로
    const env = detectEnv();
    if (e.button === 1 && env.isDesktop && ANDROID_URL) {
      window.open(ANDROID_URL, "_blank", "noopener,noreferrer");
    }
  };

  // --- iOS 버튼 ---
  const handleIOSClick = (e) => {
    const env = detectEnv();

    // 현재 정책: iOS는 배포 전 → 모든 환경에서 알림
    if (!IOS_DEPLOYED) {
      alert("iOS 앱은 현재 배포 준비 중입니다.");
      return;
    }

    // Android/Windows에서 "iOS 다운로드" 클릭 → 예외
    if (env.isAndroid || env.isWindows) {
      alert(
        "iOS 전용 링크입니다. Android/Windows에서는 사용할 수 없습니다.\n'Android 다운로드' 버튼을 이용해주세요.",
      );
      return;
    }

    // 데스크톱에서 새 탭 의도
    if (env.isDesktop && isNewTabIntent(e)) {
      if (IOS_URL) window.open(IOS_URL, "_blank", "noopener,noreferrer");
      return;
    }

    if (env.isIOS) {
      // iOS 단말: 앱 실행 시도 → 실패 시 App Store 폴백
      openIOSAppOrStore({ appStoreUrlOverride: IOS_URL });
    } else if (env.isMac) {
      // macOS 데스크톱: App Store 웹을 새 탭으로
      if (IOS_URL) window.open(IOS_URL, "_blank", "noopener,noreferrer");
      else alert("iOS App Store URL이 없습니다.");
    } else {
      // 그 외 데스크톱: 안내
      if (IOS_URL) {
        const ok = window.confirm(
          "이 링크는 iOS 전용입니다.\n새 탭으로 App Store 페이지를 여시겠습니까?",
        );
        if (ok) window.open(IOS_URL, "_blank", "noopener,noreferrer");
      } else {
        alert("iOS App Store URL이 없습니다.");
      }
    }
  };

  const handleIOSAuxClick = (e) => {
    const env = detectEnv();
    if (e.button === 1 && env.isDesktop && IOS_DEPLOYED && IOS_URL) {
      window.open(IOS_URL, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div className={cx("button-box")}>
      {/* Android 버튼 */}
      <button
        type="button"
        className={cx("button")}
        onClick={handleAndroidClick}
        onAuxClick={handleAndroidAuxClick}
        aria-label="Android 다운로드"
      >
        <img
          className={cx("button-img")}
          src="/images/google-login.png"
          alt="구글로그인"
        />
      </button>

      {/* iOS 버튼 */}
      <button
        type="button"
        className={cx("button")}
        onClick={handleIOSClick}
        onAuxClick={handleIOSAuxClick}
        aria-label="iOS 다운로드"
      >
        <img
          className={cx("button-img")}
          src="/images/apple-login.png"
          alt="애플로그인"
        />
      </button>
    </div>
  );
}
